import { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import "../../../UserDashboard/components/UserDashboardHome/UserDashboardHome.css";
import localeText from "../../../../locale";
import { Toast } from "primereact/toast";
import "../../../UserDashboard/components/StatementTable/StatementTable.css";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import { ConfirmDialog } from "primereact/confirmdialog";
import { AdvancedImage } from "@cloudinary/react";
import { fill } from "@cloudinary/url-gen/actions/resize";
import { cloudinaryInstance } from "../../../../utilities/Cloudinary/cloudinaryInstance";
import PhysicalStoreService from "../../../../service/PhysicalStoreService";

const physicalStoreService = new PhysicalStoreService();

const ListPendingStores = () => {
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [filteredStores, setFilteredStores] = useState([]);
  const [search, setSearch] = useState("");

  const [approveVisible, setApproveVisible] = useState(false);
  const [rejectVisible, setRejectVisible] = useState(false);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  const toast = useRef(null);

  const fetchStores = async (cancel = false) => {
    try {
      setLoading(true);
      const res = await physicalStoreService.getPendingVerifications();

      if (cancel) return;

      if (res.messageCode !== 200001) {
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail: localeText("data").find((msg) => msg.code === res.messageCode)?.message,
          life: 6000,
        });
      } else {
        setStores(res.result);
        setFilteredStores(res.result);
        setSearch("");
      }
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.replace("/login");
      }
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancel = false;
    fetchStores(cancel);
    return () => {
      cancel = true;
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterByName = (name) => {
    if (name) {
      setFilteredStores(
        stores.filter((s) =>
          s.verification?.physicalStoreName?.toLowerCase().includes(name.toLowerCase()),
        ),
      );
    } else {
      setFilteredStores(stores);
    }
  };

  const handleApprove = async (username) => {
    try {
      setActionLoading(true);
      const res = await physicalStoreService.approveStore(username);

      if (res.messageCode !== 200000) {
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail: localeText("data").find((msg) => msg.code === res.messageCode)?.message,
          life: 6000,
        });
      } else {
        toast.current.show({
          severity: "success",
          summary: localeText("success"),
          detail: localeText("storeApprovedSuccessfully"),
          life: 6000,
        });

        fetchStores();
      }
    } catch (e) {
      console.log(e);
    } finally {
      setActionLoading(false);
      setSelectedStore(null);
    }
  };

  const handleReject = async (username) => {
    if (!rejectionReason.trim()) {
      toast.current.show({
        severity: "warn",
        summary: localeText("error"),
        detail: localeText("enterRejectionReason"),
        life: 4000,
      });
      return;
    }

    try {
      setActionLoading(true);
      const res = await physicalStoreService.rejectStore(username, rejectionReason);

      if (res.messageCode !== 200000) {
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail: localeText("data").find((msg) => msg.code === res.messageCode)?.message,
          life: 6000,
        });
      } else {
        toast.current.show({
          severity: "success",
          summary: localeText("success"),
          detail: localeText("storeRejectedSuccessfully"),
          life: 6000,
        });
        setRejectVisible(false);
        setRejectionReason("");
        fetchStores();
      }
    } catch (e) {
      console.log(e);
    } finally {
      setActionLoading(false);
      setSelectedStore(null);
    }
  };

  const photoTemplate = (store) => {
    const photoId = store.verification?.proofPhotoId;
    const photoUrl = store.verification?.proofPhotoUrl;
    if (!photoId) return <span>—</span>;
    return (
      <a
        href={photoUrl || "#"}
        target="_blank"
        rel="noopener noreferrer"
        style={{ display: "inline-block", cursor: "pointer" }}
      >
        <AdvancedImage
          cldImg={cloudinaryInstance
            .image(photoId)
            .resize(fill().width(80).height(80))
            .format("webp")}
          height={80}
          style={{ border: "1px solid #E0E0E0", borderRadius: 6, display: "block" }}
        />
      </a>
    );
  };

  const addressTemplate = (store) => {
    const v = store.verification || {};
    const parts = [
      v.physicalAddress,
      v.physicalNumber,
      v.physicalDistrict,
      v.physicalCity,
      v.physicalState,
      v.physicalPostalCode,
      v.physicalCountry,
    ].filter(Boolean);
    return <span>{parts.join(", ")}</span>;
  };

  const statusSeverity = { PENDING: "warning", APPROVED: "success", REJECTED: "danger" };
  const statusLabel = {
    PENDING: localeText("statusPending"),
    APPROVED: localeText("statusApproved"),
    REJECTED: localeText("statusRejected"),
  };

  const statusTemplate = (store) => {
    const status = store.verification?.verificationStatus;
    if (!status) return <span>—</span>;
    return <Tag value={statusLabel[status] ?? status} severity={statusSeverity[status]} />;
  };

  const actionsTemplate = (store) => (
    <div style={{ display: "flex", gap: 8 }}>
      <Button
        icon="pi pi-eye"
        className="p-button-sm bg-blue-600 border-blue-600 hover:bg-blue-700 hover:border-blue-700"
        tooltip={localeText("viewDetails")}
        tooltipOptions={{ position: "top" }}
        onClick={() => {
          setSelectedStore(store);
          setDetailsVisible(true);
        }}
      />
    </div>
  );

  const detailsDialogFooter = (
    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }} className="pt-3">
      <Button
        label={localeText("rejectStore")}
        icon="pi pi-times"
        className="p-button-danger p-button-sm bg-red-600 border-red-600 hover:bg-red-700 hover:border-red-700"
        onClick={() => {
          setDetailsVisible(false);
          setRejectionReason("");
          setRejectVisible(true);
        }}
      />

      <Button
        label={localeText("approveStore")}
        icon="pi pi-check"
        className="p-button-success p-button-sm"
        onClick={() => {
          setDetailsVisible(false);
          setApproveVisible(true);
        }}
      />
    </div>
  );

  const rejectDialogFooter = (
    <div>
      <Button
        label={localeText("cancel")}
        icon="pi pi-times"
        className="p-button-success p-button-sm"
        onClick={() => {
          setRejectVisible(false);
          setSelectedStore(null);
          setRejectionReason("");
        }}
      />
      <Button
        label={localeText("rejectStore")}
        icon="pi pi-ban"
        className="p-button-danger p-button-sm bg-red-600 border-red-600 hover:bg-red-700 hover:border-red-700"
        loading={actionLoading}
        onClick={() => handleReject(selectedStore.verification.store.username)}
      />
    </div>
  );

  const detailsDialogContent = () => {
    if (!selectedStore) return null;
    const v = selectedStore.verification || {};
    const c = selectedStore.convention || {};

    const rowStyle = { display: "flex", gap: 8, marginBottom: 8, alignItems: "flex-start" };
    const labelStyle = { fontWeight: 600, minWidth: 180, flexShrink: 0 };

    const DetailRow = ({ label, value }) =>
      value ? (
        <div style={rowStyle}>
          <span style={labelStyle}>{label}:</span>
          <span>{value}</span>
        </div>
      ) : null;

    return (
      <div>
        <h4 className="mb-3 mt-0" style={{ borderBottom: "1px solid #e0e0e0", paddingBottom: 8 }}>
          {localeText("establishmentPhoto")}
        </h4>
        {v.proofPhotoId && (
          <div className="mb-2">
            <AdvancedImage
              cldImg={cloudinaryInstance
                .image(v.proofPhotoId)
                .resize(fill().width(200).height(200))
                .format("webp")}
              style={{
                border: "1px solid #E0E0E0",
                borderRadius: 6,
                display: "block",
                marginBottom: 8,
              }}
            />
          </div>
        )}
        {v.proofPhotoUrl && (
          <Button
            label={localeText("openPhoto")}
            icon="pi pi-external-link"
            className="p-button-outlined p-button-sm mb-4"
            onClick={() => window.open(v.proofPhotoUrl, "_blank", "noopener,noreferrer")}
          />
        )}

        <h4 className="mb-3" style={{ borderBottom: "1px solid #e0e0e0", paddingBottom: 8 }}>
          {localeText("physicalStoreName")}
        </h4>
        <DetailRow label={localeText("physicalStoreName")} value={v.physicalStoreName} />
        <DetailRow
          label={localeText("adminBackofficeAddress")}
          value={[
            v.physicalAddress,
            v.physicalNumber,
            v.physicalDistrict,
            v.physicalCity,
            v.physicalState,
            v.physicalPostalCode,
            v.physicalCountry,
          ]
            .filter(Boolean)
            .join(", ")}
        />
        <DetailRow label={localeText("verificationStatus")} value={v.verificationStatus} />
        <DetailRow
          label={localeText("submittedAt")}
          value={v.submittedAt ? new Date(v.submittedAt).toLocaleDateString() : undefined}
        />

        <h4 className="mb-3 mt-4" style={{ borderBottom: "1px solid #e0e0e0", paddingBottom: 8 }}>
          {localeText("conventionPdf")}
        </h4>
        {c.pdfUrl && (
          <Button
            label={localeText("openConvention")}
            icon="pi pi-file-pdf"
            className="p-button-outlined p-button-sm mb-3"
            onClick={() => window.open(c.pdfUrl, "_blank", "noopener,noreferrer")}
          />
        )}
        <DetailRow label={localeText("legalRepresentative")} value={c.legalRepresentativeName} />
        <DetailRow label={localeText("storeCompanyName")} value={c.companyName} />
        <DetailRow label={localeText("commercialName")} value={c.commercialName} />
        <DetailRow label={localeText("taxId")} value={c.taxId} />
        <DetailRow label={localeText("merchantCategory")} value={c.merchantCategory} />
        <DetailRow label={localeText("storePhone")} value={c.phone} />
        <DetailRow
          label={localeText("signedAt")}
          value={c.signedAt ? new Date(c.signedAt).toLocaleDateString() : undefined}
        />
        <DetailRow label={localeText("signatureIp")} value={c.ipAddress} />
      </div>
    );
  };

  return (
    <div>
      <Toast ref={toast} />

      <ConfirmDialog
        visible={approveVisible}
        onHide={() => {
          setApproveVisible(false);
          setSelectedStore(null);
        }}
        draggable={false}
        icon="pi pi-exclamation-triangle"
        message={localeText("confirmApproveStore")}
        header={localeText("approveStore")}
        acceptClassName="p-button-success"
        rejectClassName="p-button-danger bg-red-600 border-red-600 hover:bg-red-700 hover:border-red-700"
        accept={() => handleApprove(selectedStore.verification.store.username)}
        reject={() => {
          setApproveVisible(false);
          setSelectedStore(null);
        }}
        acceptLabel={localeText("confirm")}
        rejectLabel={localeText("cancel")}
      />

      <Dialog
        header={localeText("rejectStore")}
        visible={rejectVisible}
        style={{ width: "420px" }}
        draggable={false}
        footer={rejectDialogFooter}
        onHide={() => {
          setRejectVisible(false);
          setSelectedStore(null);
          setRejectionReason("");
        }}
      >
        <p className="mb-2">{localeText("enterRejectionReason")}:</p>
        <InputTextarea
          value={rejectionReason}
          onChange={(e) => setRejectionReason(e.target.value)}
          rows={4}
          style={{ width: "100%" }}
          autoResize
        />
      </Dialog>

      <Dialog
        header={localeText("storeDetails")}
        visible={detailsVisible}
        style={{ width: "560px" }}
        draggable={false}
        footer={detailsDialogFooter}
        onHide={() => {
          setDetailsVisible(false);
        }}
      >
        {detailsDialogContent()}
      </Dialog>

      <div style={{ width: "100%", display: "flex", flexDirection: "row", alignItems: "center" }}>
        <InputText
          className="w-100"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            filterByName(e.target.value);
          }}
          placeholder={localeText("searchByStoreName")}
        />
      </div>

      <hr />

      <DataTable
        className="mt-4 mb-6 p-datatable-customers"
        value={filteredStores}
        paginator
        responsiveLayout="scroll"
        loading={loading}
        dataKey="id"
        filterDisplay="row"
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate={localeText("showingFirstToLast")}
        rows={10}
        rowsPerPageOptions={[10, 20, 50]}
      >
        <Column header={localeText("image")} body={photoTemplate} style={{ width: "8%" }} />
        <Column
          field="verification.physicalStoreName"
          header={localeText("physicalStoreName")}
          sortable
          style={{ width: "20%" }}
        />
        <Column
          header={localeText("adminBackofficeAddress")}
          body={addressTemplate}
          style={{ width: "35%" }}
        />
        <Column
          field="verification.store.username"
          header={localeText("storeOwner")}
          sortable
          style={{ width: "15%" }}
        />
        <Column header={localeText("verificationStatus")} body={statusTemplate} style={{ width: "12%" }} />
        <Column
          header={localeText("requestDate")}
          body={(store) => {
            const val = store.verification?.submittedAt;
            if (!val) return <span>—</span>;
            return <span>{new Date(val).toLocaleDateString()}</span>;
          }}
          sortable
          sortField="verification.submittedAt"
          style={{ width: "13%" }}
        />
        <Column header={localeText("actions")} body={actionsTemplate} style={{ width: "17%" }} />
      </DataTable>
    </div>
  );
};

export default ListPendingStores;
